import { getWallPath } from "building-generation/elements/walls/wall";
import type { DatumAbstraction } from "building-types/datums/datumAbstractionTypes";
import type { ElevationNoteEdgeBehavior } from "building-types/edgeBehavior/edgeBehavior";
import type { Level, LevelAttributes } from "building-types/levels/levelTypes";
import type { Room } from "building-types/rooms/roomTypes";
import type { ElevationNoteTable } from "building-types/viewNotes/elevationNoteTypes";
import type { WallSystem } from "building-types/walls/wallSytemTypes";
import type { RWTable, Table } from "common/data/table";
import { isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { planarGraphHalfEdgeLine2ByFaceEdge } from "geom/planarGraph";
import { polyLine2EdgeEval, polyLine2EdgeLength, polyLine2EdgeNormal } from "geom/polyLine2";
import { vec2IsInEdgeBounds } from "geom/vec2";
import { getLevelDatumAbstractions } from "../elements/datumsLevelUtils";
import { checkWallRooms } from "../elements/walls/wall";

export function makeElevationNotes(
  levels: Table<Level>,
  levelsAttributes: Table<LevelAttributes>,
  datumAbstractions: Table<DatumAbstraction>,
  elevationNoteEdgeBehaviors: Table<ElevationNoteEdgeBehavior>,
  rooms: Table<Room>,
  walls: Table<WallSystem>,
  mirror: boolean,
): ElevationNoteTable {
  const notes: ElevationNoteTable = {};

  const resolvedDatumTable: { [levelId: string]: { [datumId: string]: number } } = {};
  for (const levelId in levels || []) {
    const levelResolvedDatumTable: { [datumId: string]: number } = {};
    const levelDatumAbstractions = getLevelDatumAbstractions(datumAbstractions, levelId);
    for (const levelDatumId in levelDatumAbstractions) {
      const levelDatumAbstraction = levelDatumAbstractions[levelDatumId];
      levelResolvedDatumTable[levelDatumId] = levelDatumAbstraction.resolveVerticalDatum({
        level: levels[levelId],
        levelAttributes: levelsAttributes[levelId],
      });
    }
    resolvedDatumTable[levelId] = levelResolvedDatumTable;
  }

  const roomElevationNotes: RWTable<ElevationNoteEdgeBehavior[]> = {};
  for (const elevationNoteId in elevationNoteEdgeBehaviors) {
    const elevationNote = elevationNoteEdgeBehaviors[elevationNoteId];
    const roomNotes = roomElevationNotes[elevationNote.parentId] ?? [];
    roomNotes.push(elevationNote);
    roomElevationNotes[elevationNote.parentId] = roomNotes;
  }

  for (const roomId in rooms) {
    const room = rooms[roomId];
    if (!room) {
      continue;
    }
    const elevationNotes = roomElevationNotes[room.id] ?? [];
    if (elevationNotes.length === 0) {
      continue;
    }

    const wallSystem = walls[room.level.id];
    const wallIndex = wallSystem.wallIndex;

    for (const elevationNoteIndex of Object.keys(elevationNotes)) {
      const elevationNote = elevationNotes[elevationNoteIndex];
      const levelDatumsTable = resolvedDatumTable[room.level.id] || {};

      const datumZ = elevationNote.datumId !== undefined ? levelDatumsTable[elevationNote.datumId] : undefined;
      const z = (datumZ ?? levels[room.level.id].floorElevation) + (elevationNote.offset ?? 0);

      const { edgeId, paramNormalized } = elevationNote;
      let { param } = elevationNote;

      const edgeIndex = room.edgeIds.findIndex((x) => x === edgeId);
      if (edgeIndex < 0) {
        continue;
      }

      if (mirror) {
        param = paramNormalized ? 1 - param : polyLine2EdgeLength(room.outline, edgeIndex) - param;
      }
      const [x, y] = polyLine2EdgeEval(room.outline, edgeIndex, param, paramNormalized);
      const edgeRefs = wallIndex.planarGraph.polygonToPlanarGraph[wallIndex.roomLevelIndices[room.id]][edgeIndex];

      let halfEdgeIndex: number | undefined;
      for (const { index, edgeIndex } of edgeRefs) {
        const halfEdge = wallIndex.planarGraph.planarGraph.faceHalfEdges[index][edgeIndex];
        const [x0, y0, x1, y1] = planarGraphHalfEdgeLine2ByFaceEdge(
          wallIndex.planarGraph.planarGraph,
          index,
          edgeIndex,
        );
        if (vec2IsInEdgeBounds(x, y, x0, y0, x1, y1, true)) {
          halfEdgeIndex = halfEdge;
        }
      }

      if (halfEdgeIndex === undefined) {
        notes[elevationNote.id] = {
          elevationNote,
          x,
          y,
          z,
          nx: undefined,
          ny: undefined,
          roomId,
          isExteriorWall: false,
        };
      } else {
        const wallId = wallIndex.halfEdgeToWall[halfEdgeIndex];
        const subWall = wallSystem.walls[wallId];

        const subWallPath = getWallPath(subWall);
        const outsideIsLeft = subWallPath.leftRoomKind > subWallPath.rightRoomKind;

        const isExteriorWall = checkWallRooms(subWall, (r) => {
          // isWalledRoomEnclosure will include portico walls in this check.
          // Switch to isInteriorRoomEnclosure to exclude them.
          return !r || !isWalledRoomEnclosure[r.attributes.roomEnclosure];
        });

        const [nx, ny] = polyLine2EdgeNormal(
          room.outline,
          edgeIndex,
          outsideIsLeft ? room === subWallPath.rightRoom : room === subWallPath.leftRoom,
        );

        notes[elevationNote.id] = {
          elevationNote,
          x,
          y,
          z,
          nx,
          ny,
          roomId,
          isExteriorWall,
        };
      }
    }
  }
  return notes;
}
